import React from 'react';

type TransferActionButtonProps = {
  children: React.ReactNode;
  /** The server's answer (`actions.<name>.allowed`). */
  allowed: boolean;
  /** Why not, when not allowed; read aloud and shown as the tooltip. */
  reason?: string | null;
  onClick: () => void;
  className: string;
  /** Another action is saving: the control waits. */
  busy?: boolean;
  /** For icon buttons. */
  ariaLabel?: string;
};

/**
 * A record action the server may refuse. When it is not allowed the control stays visible and
 * focusable but inert (aria-disabled), with the reason as its description, so it never becomes a
 * button that fails.
 */
export default function TransferActionButton({
  children,
  allowed,
  reason,
  onClick,
  className,
  busy = false,
  ariaLabel,
}: TransferActionButtonProps) {
  const reasonId = React.useId();
  const inert = !allowed || busy;
  const explanation = !allowed ? reason || 'This action is not available right now.' : null;

  function handleClick() {
    if (!inert) onClick();
  }

  return (
    <>
      <button
        type="button"
        className={className}
        aria-disabled={inert || undefined}
        aria-label={ariaLabel}
        aria-describedby={explanation ? reasonId : undefined}
        title={explanation ?? ariaLabel}
        data-busy={busy || undefined}
        onClick={handleClick}
      >
        {children}
      </button>
      {explanation ? <span id={reasonId} className="sr-only">{explanation}</span> : null}
    </>
  );
}
